import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, apiError } from "@/lib/api";
import { useExportDownload } from "@/hooks/useExportDownload";
import { ProjectPhotosTab } from "@/components/ProjectPhotosTab";
import { FilterChip } from "@/components/shared/PagePrimitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatDate, plur } from "@/lib/format";
import { FolderOpen, FolderPlus, Download, Pencil, Trash2, Check, X, FileText, Image as ImageIcon, File as FileIcon, Camera, Link2, ExternalLink, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";
import { useAuth } from "@/context/AuthContext";
import { infoSluzby, podklad, NAPOVEDA_ODKAZU } from "@/lib/odkazy";
import SlozkaDokumentu, { HvezdaTlacitko } from "@/components/docs/SlozkaDokumentu";
import KdoUvidi, { PopisViditelnosti, VYCHOZI_SKUPINY } from "@/components/docs/KdoUvidi";
import { autoPill } from "@/components/shared/Pill";

// Dokumenty zakázky: povolení, výkresy, smlouvy… + podzáložka Fotky (banka).
// Soubor leží v úložišti, tady je jen kartotéka — viz backend project_docs.py.
// 268: hlavní cesta je odkaz na disk firmy (složka zakázky + odkazy na soubory);
// nahrání do aplikace zůstává jako vedlejší malé tlačítko.
// 313: složek může být víc (první ze 268 leží na zakázce, další jako karty `folder`)
// a hvězdička dělá z odkazu nebo složky hlavní dokument v Informacích zakázky.

const fmtSize = (b) => {
  if (b == null) return "";
  if (b >= 1024 * 1024) return `${(b / 1048576).toFixed(1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(b / 1024))} kB`;
};
const iconFor = (d) => {
  const ct = d.content_type || "";
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct === "application/pdf") return FileText;
  return FileIcon;
};

export function ProjectDocsTab({ projectId, manager }) {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "fotky" ? "fotky" : "dokumenty";
  const setView = (v) => { const n = new URLSearchParams(params); n.set("view", v); setParams(n, { replace: true }); };

  const [data, setData] = useState(null);
  const [cat, setCat] = useState("all");
  // 271: dokument se stahuje jako blob — chyba skončí hláškou, ne odchodem z aplikace
  const { busy: downloading, run: runDownload } = useExportDownload();
  const [edit, setEdit] = useState(null);     // {id, name}
  const [linkDlg, setLinkDlg] = useState(null);   // 268: {id?, url, name, category, komu, skupiny, ai, can_manage}
  const [linkBusy, setLinkBusy] = useState(false);
  const [team, setTeam] = useState([]);
  const { user } = useAuth();
  // Ovládání vnořené galerie fotek — hlavní tlačítko je v hlavičce téhle karty.
  const [photoApi, setPhotoApi] = useState(null);
  const onPhotoApi = useCallback((a) => setPhotoApi(a), []);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/projects/${projectId}/documents-files`); setData(d); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  // 268: lidé pro „Kdo uvidí“ — jen kdo smí dokumenty upravovat
  useEffect(() => {
    if (!data?.can_edit || team.length) return;
    api.get("/team").then((r) => setTeam((r.data || []).filter((m) => m.status !== "inactive"))).catch(() => {});
  }, [data?.can_edit, team.length]);

  const saveFolder = async (body) => {
    await api.patch(`/projects/${projectId}/documents-folder`, body);
    await load();
  };
  const openNewLink = () => setLinkDlg({ mode: "file", url: "", name: "", category: cat !== "all" ? cat : "vykresy", komu: [], skupiny: VYCHOZI_SKUPINY, ai: false, can_manage: true });
  // 269: složka se připojuje ve stejném okně jako odkaz (přepínač Soubor / Složka).
  // 313: bez `f` = nová složka (karta), `f.legacy` = první složka uložená na zakázce.
  const folderDlg = (f) => ({ mode: "folder", id: f?.legacy ? null : f?.id, legacy: !!f?.legacy, url: f?.url || "", name: f?.legacy ? "" : f?.name || "",
    komu: f?.komu || [], skupiny: f ? f.skupiny || [] : VYCHOZI_SKUPINY, ai: !!f?.ai, can_manage: f?.legacy || !f ? true : !!f.can_manage });
  const setMode = (m) => setLinkDlg((x) => (m === "folder" ? folderDlg() : { ...x, mode: "file", url: "", komu: [], skupiny: VYCHOZI_SKUPINY, ai: false }));
  const removeFolder = async () => {
    if (!linkDlg.legacy) {
      const f = (data.documents || []).find((d) => d.id === linkDlg.id);
      setLinkDlg(null);
      if (f) remove(f);
      return;
    }
    setLinkBusy(true);
    try { await saveFolder({ slozka_url: "" }); setLinkDlg(null); toast.success("Složka odebrána. Soubory na disku firmy zůstaly."); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLinkBusy(false); }
  };
  const toggleStar = async (d) => {
    try {
      if (d.legacy) await api.patch(`/projects/${projectId}/documents-folder`, { slozka_star: !d.star });
      else await api.patch(`/project-documents/${d.id}`, { star: !d.star });
      toast.success(d.star ? "Odebráno z hlavních dokumentů." : "V Informacích zakázky mezi hlavními dokumenty.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveLink = async () => {
    setLinkBusy(true);
    try {
      if (linkDlg.mode === "folder" && linkDlg.legacy) {
        await saveFolder({ slozka_url: linkDlg.url.trim(), slozka_komu: linkDlg.komu, slozka_skupiny: linkDlg.skupiny, slozka_ai: linkDlg.ai });
        toast.success("Složka uložena.");
        setLinkDlg(null);
        return;
      }
      if (linkDlg.mode === "folder" && !linkDlg.id) {
        await api.post(`/projects/${projectId}/documents-links`, {
          url: linkDlg.url, name: linkDlg.name, folder: true, komu: linkDlg.komu, skupiny: linkDlg.skupiny, ai: linkDlg.ai });
        toast.success("Složka připojena.");
        setLinkDlg(null); load();
        return;
      }
      if (linkDlg.id) {
        const body = linkDlg.mode === "folder" ? { name: linkDlg.name } : { name: linkDlg.name, category: linkDlg.category };
        if (linkDlg.can_manage) Object.assign(body, { komu: linkDlg.komu, skupiny: linkDlg.skupiny, ai: linkDlg.ai });
        await api.patch(`/project-documents/${linkDlg.id}`, body);
      } else {
        await api.post(`/projects/${projectId}/documents-links`, {
          url: linkDlg.url, name: linkDlg.name, category: linkDlg.category, komu: linkDlg.komu, skupiny: linkDlg.skupiny, ai: linkDlg.ai });
        toast.success("Odkaz přidán.");
      }
      setLinkDlg(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLinkBusy(false); }
  };

  // Jedna editace řeší název i kategorii — přesun mezi kategoriemi je tak
  // stejná tužka jako přejmenování, žádná zvláštní cesta.
  const saveEdit = async () => {
    try { await api.patch(`/project-documents/${edit.id}`, { name: edit.name, category: edit.category }); setEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (d) => {
    const q = d.folder ? `Odebrat složku „${d.name}"? Složka na disku firmy zůstane.`
      : d.kind === "LINK" ? `Odebrat odkaz „${d.name}"? Soubor na disku firmy zůstane.` : `Smazat dokument „${d.name}"? Nejde to vrátit.`;
    if (!await ask(q)) return;
    try { await api.delete(`/project-documents/${d.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Načítám dokumenty…</p>;

  const cats = data.categories || [];
  const catLabel = Object.fromEntries(cats.map((c) => [c.value, c.label]));
  const files = (data.documents || []).filter((d) => !d.folder);
  const folders = [...(data.folder ? [{ ...data.folder, id: "slozka", legacy: true }] : []), ...(data.documents || []).filter((d) => d.folder)];
  const docs = files.filter((d) => cat === "all" || d.category === cat);
  const countBy = {};
  files.forEach((d) => { countBy[d.category] = (countBy[d.category] || 0) + 1; });
  const canEdit = !!data.can_edit;
  const photoView = view === "fotky" && manager;

  return (
    <div className="bg-card border border-border rounded-[16px] p-4 md:p-5" data-testid="project-docs-tab-card">
      {/* Jednotná hlavička modulu: vlevo název a drobná meta, vpravo VŽDY na
          stejném místě hlavní akce. Mění se jen její popisek podle podzáložky,
          tlačítko tak nikam neposkakuje. */}
      <div className="flex items-start gap-3 flex-wrap mb-4">
        <div className="min-w-0">
          <h2 className="text-[19px] font-black tracking-tight flex items-center gap-2">
            <FolderOpen size={18} className="text-brand" /> Dokumenty
          </h2>
          <p className="text-[13px] text-muted-foreground mt-0.5" data-testid="docs-meta">
            {photoView
              ? plur(photoApi?.total || 0, "fotka", "fotky", "fotek")
              : [folders.length ? plur(folders.length, "složka", "složky", "složek") : null, plur(files.length, "dokument", "dokumenty", "dokumentů")].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex-1" />
        {photoView && manager && (
          <Button onClick={() => photoApi?.pick?.()} disabled={!!photoApi?.busy}
            className="h-[42px] w-full md:w-auto font-bold" data-testid="docs-upload-btn">
            <Camera size={16} /> {photoApi?.busy ? (photoApi?.progress ? `Nahrávám ${photoApi.progress.done}/${photoApi.progress.total}…` : "Nahrávám…") : "Nahrát fotky"}
          </Button>
        )}
        {/* 271: dokumenty jsou jen odkazy na disk firmy — nahrávat soubory do aplikace už nejde */}
        {!photoView && canEdit && (
          <div className="flex gap-2 w-full md:w-auto">
            <Button variant="outline" onClick={() => setLinkDlg(folderDlg())} className="h-[42px] flex-1 md:flex-none font-bold" data-testid="docs-folder-btn">
              <FolderPlus size={16} /> Složka
            </Button>
            <Button onClick={openNewLink} className="h-[42px] flex-1 md:flex-none font-bold" data-testid="docs-link-btn">
              <Link2 size={16} /> Odkaz na soubor
            </Button>
          </div>
        )}
      </div>

      {/* Podzáložky — Fotky jen pro vedení (galerie čte endpoint pro vedení).
          Stejný přepínač jako v Ekonomice → Platby → Provedené platby: vybraná
          část je bílá (v tmavém režimu barva karty), zbytek splývá s pozadím. */}
      {manager && (
        <div ref={autoPill} className="inline-flex gap-1 p-1 rounded-full bg-secondary border border-border mb-4">
          {[["dokumenty", "Dokumenty"], ["fotky", "Fotky"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setView(v)} data-testid={`docs-subtab-${v}`} data-pill-on={view === v || undefined}
              className={`h-10 px-4 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${
                view === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      {photoView ? (
        <ProjectPhotosTab projectId={projectId} canEdit={manager} embedded headless onApi={onPhotoApi} />
      ) : (
        <>
          {/* 271: dřív nahrané soubory jdou dál stáhnout — ať si je lidé přesunou na disk firmy */}
          {canEdit && (data.documents || []).some((d) => d.kind !== "LINK") && (
            <div className="flex gap-3 rounded-[14px] border border-amber-300/70 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 p-3.5 mb-4" data-testid="docs-upload-ended">
              <TriangleAlert size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="text-[13px] leading-snug min-w-0">
                <div className="font-bold">Nahrávání souborů do Proyekty skončilo</div>
                <div className="text-muted-foreground mt-0.5">Nahrané soubory si stáhněte a přesuňte na disk firmy. Nové soubory přidávejte jako odkaz.</div>
              </div>
            </div>
          )}

          {folders.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="docs-folder">
              {folders.map((f) => (
                <SlozkaDokumentu key={f.id} folder={f} team={team} testid={f.legacy ? "slozka" : `slozka-${f.id}`}
                  onStar={canEdit ? () => toggleStar(f) : null} onUpravit={canEdit ? () => setLinkDlg(folderDlg(f)) : null} />
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap mb-4">
            <FilterChip label={`Vše (${files.length})`} active={cat === "all"} onClick={() => setCat("all")} testid="docs-cat-all" />
            {cats.map((c) => (
              <FilterChip key={c.value} label={`${c.label} (${countBy[c.value] || 0})`} active={cat === c.value}
                onClick={() => setCat(c.value)} testid={`docs-cat-${c.value}`} />
            ))}
          </div>

          {!docs.length && (
            <p className="text-sm text-muted-foreground" data-testid="docs-empty">
              {cat === "all" ? "Zatím žádné dokumenty. Přidej odkaz na soubor na disku firmy — povolení, výkresy, smlouvy."
                : "V této kategorii zatím nic není."}
            </p>
          )}

          <div className="divide-y divide-border border border-border rounded-[14px] overflow-hidden empty:border-0" data-testid="docs-list">
            {docs.map((d) => {
              const Icon = iconFor(d);
              const isLink = d.kind === "LINK";
              const sl = isLink ? infoSluzby(d.service || d.url) : null;
              return (
                <div key={d.id} className="flex items-center gap-3 p-3 min-h-[56px] hover:bg-secondary/40" data-testid={`doc-${d.id}`}>
                  {isLink ? (
                    <span className="h-8 min-w-[32px] px-1 rounded-[9px] grid place-items-center text-[10.5px] font-extrabold tracking-wide shrink-0 dark:brightness-[1.7]"
                      style={{ background: podklad(sl.barva), color: sl.barva }} title={sl.nazev} data-testid={`doc-service-${d.id}`}>{sl.zkratka}</span>
                  ) : <Icon size={20} className="text-brand shrink-0" />}
                  <div className="min-w-0 flex-1">
                    {edit?.id === d.id ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }} autoFocus
                          className="h-9 text-sm min-w-0 flex-1 basis-[160px]" data-testid={`doc-name-input-${d.id}`} />
                        <Select value={edit.category} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                          <SelectTrigger className="h-9 w-[130px] text-sm" data-testid={`doc-cat-select-${d.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{cats.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <button type="button" onClick={saveEdit} className="w-9 h-9 grid place-items-center text-success shrink-0" data-testid={`doc-name-save-${d.id}`}><Check size={16} /></button>
                        <button type="button" onClick={() => setEdit(null)} className="w-9 h-9 grid place-items-center text-muted-foreground shrink-0"><X size={16} /></button>
                      </div>
                    ) : (
                      isLink ? (
                        <a href={d.url} target="_blank" rel="noopener noreferrer" className="block min-w-0 group" data-testid={`doc-open-name-${d.id}`}>
                          <div className="text-sm font-semibold truncate group-hover:text-brand">{d.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {sl.nazev} · {catLabel[d.category] || d.category} · {formatDate(d.created_at)}
                            <PopisViditelnosti komu={d.komu} skupiny={d.skupiny} ai={d.ai} team={team} />
                          </div>
                        </a>
                      ) : (
                      <>
                        <div className="text-sm font-semibold truncate">{d.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {catLabel[d.category] || d.category}{d.size ? ` · ${fmtSize(d.size)}` : ""} · {d.uploaded_by_name} · {formatDate(d.created_at)}
                        </div>
                      </>
                      )
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {isLink ? (
                      <a href={d.url} target="_blank" rel="noopener noreferrer" title="Otevřít"
                        className="w-10 h-10 hidden sm:grid place-items-center text-muted-foreground hover:text-brand" data-testid={`doc-open-${d.id}`}>
                        <ExternalLink size={16} />
                      </a>
                    ) : (
                    <button type="button" title="Stáhnout" disabled={!!downloading} onClick={() => runDownload(d.id, d.url, d.name, "Stahuji dokument…")}
                      className="w-10 h-10 grid place-items-center text-muted-foreground hover:text-brand" data-testid={`doc-download-${d.id}`}>
                      <Download size={16} />
                    </button>
                    )}
                    {canEdit && (
                      <>
                        <HvezdaTlacitko star={d.star} onClick={() => toggleStar(d)} testid={`doc-star-${d.id}`} />
                        <button type="button" title="Upravit" onClick={() => (isLink
                          ? setLinkDlg({ id: d.id, url: d.url, name: d.name, category: d.category, komu: d.komu || [], skupiny: d.skupiny || [], ai: !!d.ai, can_manage: !!d.can_manage })
                          : setEdit({ id: d.id, name: d.name, category: d.category }))}
                          className="w-10 h-10 grid place-items-center text-muted-foreground hover:text-brand" data-testid={`doc-rename-${d.id}`}>
                          <Pencil size={15} />
                        </button>
                        <button type="button" title="Smazat" onClick={() => remove(d)}
                          className="w-10 h-10 grid place-items-center text-muted-foreground hover:text-destructive" data-testid={`doc-del-${d.id}`}>
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

        </>
      )}

      {/* 268: odkaz na soubor na disku firmy — přidání i úprava */}
      <Dialog open={!!linkDlg} onOpenChange={(o) => { if (!o) setLinkDlg(null); }}>
        <DialogContent className="max-w-[560px] max-h-[90dvh] overflow-y-auto" data-testid="docs-link-dialog">
          <DialogHeader>
            <DialogTitle>{linkDlg?.mode === "folder" ? (linkDlg.id || linkDlg.legacy ? "Upravit složku" : "Přidat složku") : linkDlg?.id ? "Upravit odkaz" : "Odkaz na soubor"}</DialogTitle>
            <DialogDescription>{linkDlg?.mode === "folder" ? "Složka na disku firmy — v Dokumentech bude nahoře s tlačítkem Otevřít. Hvězdičkou ji dáte i do Informací zakázky." : "Soubor zůstává na disku firmy — Proyekty si pamatují jen odkaz."}</DialogDescription>
          </DialogHeader>
          {linkDlg && !linkDlg.id && !linkDlg.legacy && (
            <div ref={autoPill} className="inline-flex w-fit justify-self-start gap-1 p-1 rounded-full bg-secondary border border-border" role="group" aria-label="Co přidáš">
              {[["file", "Soubor", Link2], ["folder", "Složka", FolderOpen]].map(([m, l, Ic]) => (
                <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={linkDlg.mode === m} data-testid={`doc-link-mode-${m}`}
                  className={`h-10 md:h-9 px-4 rounded-full text-sm font-bold whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${
                    linkDlg.mode === m ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                  <Ic size={15} /> {l}
                </button>
              ))}
            </div>
          )}
          {linkDlg && linkDlg.mode === "folder" && (
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); saveLink(); }}>
              <div className={`grid grid-cols-1 ${linkDlg.legacy ? "" : "lg:grid-cols-[3fr_2fr]"} gap-3`}>
                <div className="min-w-0">
                  <Label htmlFor="doc-folder-url">Odkaz na složku</Label>
                  <Input id="doc-folder-url" type="url" inputMode="url" autoFocus={!linkDlg.id} disabled={!!linkDlg.id} value={linkDlg.url}
                    onChange={(e) => setLinkDlg({ ...linkDlg, url: e.target.value })}
                    placeholder="https://firma.sharepoint.com/…" className="h-11 mt-1 w-full min-w-0 text-base md:text-sm" data-testid="doc-folder-url" />
                </div>
                {!linkDlg.legacy && (
                  <div className="min-w-0">
                    <Label htmlFor="doc-folder-name">Název <span className="font-normal text-muted-foreground">(nepovinné)</span></Label>
                    <Input id="doc-folder-name" value={linkDlg.name} onChange={(e) => setLinkDlg({ ...linkDlg, name: e.target.value })}
                      placeholder="např. Výkresy" className="h-11 mt-1 w-full min-w-0 text-base md:text-sm" data-testid="doc-folder-name" />
                  </div>
                )}
              </div>
              {!linkDlg.id && <p className="text-[12.5px] text-muted-foreground -mt-2">{NAPOVEDA_ODKAZU}</p>}
              {linkDlg.can_manage ? (
                <KdoUvidi team={team} meId={user?.id} komu={linkDlg.komu} skupiny={linkDlg.skupiny} ai={linkDlg.ai} testid="doc-folder-kdo"
                  onSkupiny={(k) => setLinkDlg((x) => ({ ...x, skupiny: k }))} onKomu={(k) => setLinkDlg((x) => ({ ...x, komu: k }))} onAi={(v) => setLinkDlg((x) => ({ ...x, ai: v }))} />
              ) : (
                <p className="text-[12.5px] text-muted-foreground">Kdo složku uvidí a jestli ji smí AI, mění ten, kdo ji vložil, nebo majitel.</p>
              )}
              <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-end gap-2 pt-1">
                {(linkDlg.id || (linkDlg.legacy && data?.folder?.url)) && (
                  <button type="button" onClick={removeFolder} disabled={linkBusy} data-testid="doc-folder-remove"
                    className="h-11 md:h-10 px-2 text-[13px] font-semibold text-muted-foreground hover:text-destructive md:mr-auto">Odebrat složku</button>
                )}
                <Button type="button" variant="outline" onClick={() => setLinkDlg(null)} className="h-12 md:h-10 w-full md:w-auto">Zrušit</Button>
                <Button type="submit" disabled={linkBusy || !linkDlg.url.trim()} className="h-12 md:h-10 w-full md:w-auto font-bold" data-testid="doc-folder-save">
                  {linkBusy ? "Ukládám…" : linkDlg.id || linkDlg.legacy ? "Uložit" : "Připojit složku"}
                </Button>
              </div>
            </form>
          )}
          {linkDlg && linkDlg.mode !== "folder" && (
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); saveLink(); }}>
              <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-3">
                <div className="min-w-0">
                  <Label htmlFor="doc-link-url">Odkaz</Label>
                  <Input id="doc-link-url" type="url" inputMode="url" autoFocus={!linkDlg.id} disabled={!!linkDlg.id}
                    value={linkDlg.url} onChange={(e) => setLinkDlg({ ...linkDlg, url: e.target.value })}
                    placeholder="https://firma.sharepoint.com/…" className="h-11 mt-1 w-full min-w-0 text-base md:text-sm" data-testid="doc-link-url" />
                </div>
                <div className="min-w-0">
                  <Label htmlFor="doc-link-name">Název <span className="font-normal text-muted-foreground">(nepovinné)</span></Label>
                  <Input id="doc-link-name" value={linkDlg.name} onChange={(e) => setLinkDlg({ ...linkDlg, name: e.target.value })}
                    placeholder="Vezme se z konce odkazu" className="h-11 mt-1 w-full min-w-0 text-base md:text-sm" data-testid="doc-link-name" />
                </div>
              </div>
              {!linkDlg.id && <p className="text-[12.5px] text-muted-foreground -mt-2">{NAPOVEDA_ODKAZU}</p>}
              <div>
                <Label>Kategorie</Label>
                <Select value={linkDlg.category} onValueChange={(v) => setLinkDlg({ ...linkDlg, category: v })}>
                  <SelectTrigger className="h-11 mt-1 w-full sm:w-[220px]" data-testid="doc-link-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {linkDlg.can_manage ? (
                <KdoUvidi team={team} meId={user?.id} komu={linkDlg.komu} skupiny={linkDlg.skupiny} ai={linkDlg.ai} testid="doc-link-kdo"
                  onSkupiny={(k) => setLinkDlg((x) => ({ ...x, skupiny: k }))} onKomu={(k) => setLinkDlg((x) => ({ ...x, komu: k }))} onAi={(v) => setLinkDlg((x) => ({ ...x, ai: v }))} />
              ) : (
                <p className="text-[12.5px] text-muted-foreground">Kdo odkaz uvidí a jestli ho smí AI, mění ten, kdo ho vložil, nebo majitel.</p>
              )}
              <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setLinkDlg(null)} className="h-12 md:h-10 w-full md:w-auto">Zrušit</Button>
                <Button type="submit" disabled={linkBusy || (!linkDlg.id && !linkDlg.url.trim())}
                  className="h-12 md:h-10 w-full md:w-auto font-bold" data-testid="doc-link-save">
                  {linkBusy ? "Ukládám…" : linkDlg.id ? "Uložit" : "Přidat odkaz"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
